import React, { useMemo } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip as RechartsTooltip,
	Legend,
	CartesianGrid,
	ResponsiveContainer,
} from "recharts";
import { formatValue } from "../Utils.js";
import _ from "underscore";
import moment from "moment";
import SmartRechartsTooltip from "./SmartRechartsTooltip.jsx";

const fillColors = [
	"hsl(var(--chart-1))",
	"hsl(var(--chart-2))",
	"hsl(var(--chart-3))",
	"hsl(var(--chart-teal))",
	"hsl(var(--primary))",
	"hsl(var(--chart-1))",
	"hsl(var(--chart-2))",
	"hsl(var(--chart-3))",
	"hsl(var(--chart-teal))",
	"hsl(var(--primary))",
	"hsl(var(--chart-1))",
];


function prepareData(data) {
	let d = _.groupBy(data, "date_used");
	let result = [];
	let count = 0;

	for (let key in d) {
		let o = {};
		let maxTotal = 0;
		let totalPlusChange = 0;
		let totalMinChange = 0;
		let isFirst = count === 0;
		let isLast = count === Object.keys(d).length - 1;

		for (const item of d[key]) {
			o[item.grouping] = item.total;
			o[item.grouping + "_plus_change"] = item.plus_change;
			o[item.grouping + "_min_change"] = item.min_change;
			totalPlusChange += item.plus_change;
			totalMinChange += item.min_change;
			maxTotal = Math.max(maxTotal, item.total);
		}

		result.push({
			_name: moment(key).format("MMM D"),
			_original: key,
			_totalPlusChange: totalPlusChange,
			_totalMinChange: totalMinChange,
			_maxTotal: maxTotal,
			_isFirst: isFirst,
			_isLast: isLast,
			...o,
		});

		count += 1;
	}

	result = result.sort(function (a, b) {
		return new Date(a._original) - new Date(b._original);
	});

	return result;
}

function getSortedHeaders(data) {
	if (data !== null && data !== undefined && data.length > 0) {
		let object = {};

		// create an object with all the headers and summarize the values
		for (const item of data) {
			if (object[item.grouping] === undefined) {
				object[item.grouping] = 0;
			} else {
				object[item.grouping] += item.measure;
			}
		}

		let values = [];
		for (const key in object) {
			if (key !== "_itchange") {
				values.push({
					header: [key][0],
					value: object[key],
				});
			}
		}

		let sorted = values.sort((a, b) => b.value - a.value);
		return _.pluck(sorted, "header");
	}
}

// Custom dot renderer: only show dot if there is a change
function ConditionalDot({ cx, cy, payload, dataKey, stroke }) {
	const plusChange = payload[dataKey + "_plus_change"];
	const minChange = payload[dataKey + "_min_change"];
	if ((plusChange && plusChange !== 0) || (minChange && minChange !== 0)) {
		return (
			<circle cx={cx} cy={cy} r={5} fill={stroke} stroke="#fff" strokeWidth={1.5} />
		);
	}
	return null;
}

export const LineChartComponent = (props) => {
	const { data, title,   useOwnBackground = true } = props;

	const pdata = data ? prepareData(data) : [];
	const sortedHeaders = useMemo(() => (data ? getSortedHeaders(data) || [] : []), [data]);
	let maxValue = 0;

	if (pdata) {
		pdata.forEach(item => {
			maxValue = Math.max(maxValue, item._maxTotal);
		});
	}

	// Create lines with activeDot component
	const lines = useMemo(() => {
		return sortedHeaders.map((dataKey, h) => {
			const color = fillColors[h % fillColors.length] || "hsl(var(--chart-1))";
			
			// Create activeDot component
			const activeDotComponent = (props) => {
				return (
					<circle
						cx={props.cx}
						cy={props.cy}
						r={6}
						fill={color}
						stroke="#fff"
						strokeWidth={2}
					/>
				);
			};

			return (
				<Line
					type="linear"
					key={h}
					dataKey={dataKey}
					fill={color}
					stroke={color}
					strokeWidth={2}
					dot={(props) => <ConditionalDot {...props} dataKey={dataKey} stroke={color}/>}
					activeDot={activeDotComponent}
					isAnimationActive={false}
				/>
			);
		});
	}, [sortedHeaders]);

	// Build tooltip data from Recharts tooltip props
	const buildTooltipData = (props) => {
		if (!props || !props.payload || props.payload.length === 0) {
			return null;
		}

		// Get the first payload item to access the data point
		const firstPayload = props.payload[0];
		const dataPoint = firstPayload?.payload;
		
		if (!dataPoint) return null;

		// Extract date/time information - use props.label as fallback
		const label = props.label || dataPoint._name || '';
		const original = dataPoint._original || dataPoint._name || props.label || '';

		// Filter payload to only include items with valid data
		// Include all items that have a dataKey and a value (even if 0) or have changes
		const filteredPayload = props.payload.filter(item => {
			if (!item.dataKey) return false;
			const value = item.value;
			const plusChange = dataPoint[item.dataKey + "_plus_change"];
			const minChange = dataPoint[item.dataKey + "_min_change"];
			const hasValidPlusChange = Number.isFinite(plusChange) && plusChange !== 0;
			const hasValidMinChange = Number.isFinite(minChange) && minChange !== 0;
			// Include if value exists (even if 0) or if there are changes
			const hasValue = value !== undefined && value !== null;
			return hasValue || hasValidPlusChange || hasValidMinChange;
		});

		// If no filtered payload, try to include all items with dataKey (more permissive)
		if (filteredPayload.length === 0) {
			const allWithDataKey = props.payload.filter(item => item.dataKey);
			if (allWithDataKey.length > 0) {
				// Use all items that have a dataKey
				const payload = allWithDataKey.map(item => {
					const headerIndex = sortedHeaders.indexOf(item.dataKey);
					const color = headerIndex >= 0 ? fillColors[headerIndex % fillColors.length] : (item.fill || item.color || "hsl(var(--chart-1))");
					return {
						name: item.dataKey,
						value: item.value !== undefined && item.value !== null ? item.value : 0,
						fill: color,
						payload: dataPoint
					};
				});

				return {
					label: label,
					original: original,
					payload: payload
				};
			}
			return null;
		}

		// Map payload items with correct colors based on sortedHeaders index
		const payload = filteredPayload.map(item => {
			const headerIndex = sortedHeaders.indexOf(item.dataKey);
			const color = headerIndex >= 0 ? fillColors[headerIndex % fillColors.length] : (item.fill || item.color || "hsl(var(--chart-1))");
			return {
				name: item.dataKey,
				value: item.value !== undefined && item.value !== null ? item.value : 0,
				fill: color,
				payload: dataPoint
			};
		});

		return {
			label: label,
			original: original,
			payload: payload
		};
	};

	// Render tooltip content
	const renderTooltipContent = ({ data }) => {
		return <CustomTooltipContent tooltipData={data} />;
	};

	// Widget mode: use flex layout with responsive height (like BarChartComponent_v2)
	if (!useOwnBackground) {
		return (
			<div
				className="flex flex-col h-full flex-1 overflow-hidden bg-card rounded-card border border-card-bd p-4"
			>
				{pdata && pdata.length > 0 ? (
					<div className="flex-1 min-h-0 relative">
						{title && (
							<div className="absolute top-0 left-0 z-10 p-2 pointer-events-none">
								<div className="text-text text-lg font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
									{title}
								</div>
							</div>
						)}
						<div className="absolute inset-0 top-8">
							<ResponsiveContainer width="100%" height="100%">
								<LineChart
									data={pdata}
									margin={{
										top: 30,
										left: 0,
										bottom: 20,
										right: 40,
									}}
								>
									<CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
									<XAxis dataKey="_name" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" />
									<YAxis tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={formatYAxis}/>
									<RechartsTooltip
										cursor={false}
										content={SmartRechartsTooltip.createContentCallback({
											buildTooltipData,
											renderContent: renderTooltipContent,
											estimatedWidth: 280,
											estimatedHeight: 200,
											offsetX: 50,
											offsetY: -175,
										})}
									/>

									{lines}

									<Legend
										verticalAlign="bottom"
										align="center"
										iconType="circle"
										formatter={renderLegend}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					</div>
				) : (
					<div className="flex-1 min-h-0 relative">
						<div className="absolute inset-0 flex items-center justify-center text-text-2">
							No data available
						</div>
					</div>
				)}
			</div>
		);
	}

	// Standalone mode: fixed height with margins (original behavior)
	return (
		<div
			className="bg-card rounded-card border border-card-bd p-4 my-2 mr-6 mb-8"
		>
			{title && (
				<div className="text-text text-lg font-semibold mt-4 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
					{title}
				</div>
			)}
            <ResponsiveContainer width="100%" height={340}>
                <LineChart
                    data={pdata}
                    margin={{
                        top: 30,
                        left: 0,
                        bottom: 20,
                        right: 40,
                    }}
                >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
                    <XAxis dataKey="_name" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" />
                    <YAxis tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={formatYAxis}/>
                    <RechartsTooltip
						cursor={false}
						content={SmartRechartsTooltip.createContentCallback({
							buildTooltipData,
							renderContent: renderTooltipContent,
							estimatedWidth: 280,
							estimatedHeight: 200,
							offsetX: 50,
							offsetY: -175,
						})}
					/>

                    {lines}

                    <Legend
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        formatter={renderLegend}
                    />
                </LineChart>
            </ResponsiveContainer>
		</div>
	);
};

function formatYAxis(value) {
	return formatValue(value, "decimal", true);
}

const renderLegend = (value, entry) => {
	return <span className="text-text-2 text-xs">{value}</span>;
};

// Custom tooltip content component
const CustomTooltipContent = ({ tooltipData }) => {
	if (!tooltipData || !tooltipData.payload || tooltipData.payload.length === 0) {
		return null;
	}

	const { payload } = tooltipData;

	// Determine if any row has a nonzero plus change or min change
	let showPlusChange = false;
	let showMinChange = false;
	for (const item of payload) {
		const plusChange = item.payload[item.name + "_plus_change"];
		const minChange = item.payload[item.name + "_min_change"];
		if (Number.isFinite(plusChange) && plusChange !== 0) showPlusChange = true;
		if (Number.isFinite(minChange) && minChange !== 0) showMinChange = true;
	}

	// Build grid template columns
	let gridColumns = "15px 1fr 1fr";
	if (showPlusChange) gridColumns += " 1fr";
	if (showMinChange) gridColumns += " 1fr";

	// Build header
	const headerCells = [
		<span key="icon" />,
		<span className="text-sm text-text-2 mt-0 pt-0" key="cluster">Resource cluster</span>,
		<span className="text-sm text-text-2 text-right" key="resources"># resources</span>,
	];
	if (showMinChange) headerCells.push(
		<span className="text-sm text-text-2 text-right" key="min">min change</span>
	);
	if (showPlusChange) headerCells.push(
		<span className="text-sm text-text-2 text-right" key="plus">plus change</span>
	);
	const header = (
		<div className="grid w-full mb-0 text-text-2" style={{gridTemplateColumns: gridColumns}}>
			{headerCells}
		</div>
	);

	// Build rows
	const items = payload.map((item, i) => {
		const plusChange = item.payload[item.name + "_plus_change"];
		const minChange = item.payload[item.name + "_min_change"];
		const hasValidPlusChange = Number.isFinite(plusChange) && plusChange !== 0;
		const hasValidMinChange = Number.isFinite(minChange) && minChange !== 0;
		const rowCells = [
			<svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" className="" key="icon" aria-hidden="true" focusable="false" role="presentation">
				<rect x="0" y="8" width="6" height="6" fill={item.fill} />
			</svg>,
			<span className="flex-1 mr-4" key="name">{`${item.name}`}</span>,
			<span className="text-right" key="resources">{`${formatValue(item.value, "decimal", false)}`}</span>,
		];
		if (showMinChange) rowCells.push(
			<span className="ml-6 text-right" key="min">{hasValidMinChange ? `${formatValue(minChange, "decimal", true)}` : ''}</span>
		);
		if (showPlusChange) rowCells.push(
			<span className="ml-6 text-right" key="plus">{hasValidPlusChange ? `${formatValue(plusChange, "decimal", true)}` : ''}</span>
		);
		return (
			<div className="grid w-full text-text-2" style={{gridTemplateColumns: gridColumns}} key={i}>
				{rowCells}
			</div>
		);
	});

	return (
		<>
			<div className="flex flex-row mb-2 text-text-2">
				<p className="flex-1 label font-semibold mr-4">
					{`${moment(tooltipData.original).format("dddd, MMMM D")}`}
				</p>
			</div>
			{header}
			{items}
			{payload[0]?.payload?._change && (
				<div className="mt-2">
					<p className="text-text-2 text-sm">
						{`${payload[0]?.payload?._change}`}
					</p>
				</div>
			)}
		</>
	);
};
